import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { useMemo, useState } from 'react';
import { RxUpdate } from 'react-icons/rx';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import { useProvinceContext } from '../../../contexts/ProvinceProvider';
import useProvincesActions from '../../../hooks/APIHooks/users/useProvincesActions';
import { addEditProvinceSchema } from '../../../schemas';
import { SelectData } from '../../../types/common';
import { Province, ProvinceReqBody } from '../../../types/province';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaCountrySelect from '../Inputs/AcademiaCountrySelect';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaModalLoading from '../Loadings/AcademiaModalLoading';
import NoTableDataFound from '../NoTableDataFound';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const EditProvinceModal = ({
  province,
  handleModalClose,
}: {
  province: Province;
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [selectedCountry, setSelectedCountry] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );

  // integration of custom API hooks here
  const { getCountryWiseProvincesHandler, updateProvinceByIdHandler } =
    useProvincesActions('', selectedCountry.name);

  // integration of context hooks here
  const { provinces, setTargetedProvince, setSearchText } =
    useProvinceContext();
  const { currentUser } = useAuthContext();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  const provinceOptions = useMemo(() => {
    if (!getCountryWiseProvincesHandler.data?.data) return [];

    return getCountryWiseProvincesHandler.data.data
      .filter(
        (province) =>
          !provinces.some(
            (addedProvince) =>
              addedProvince.province_name === province.province_name
          )
      )
      .map((province, index) => ({
        id: index,
        name: province.province_name,
        code: province.province_code,
      }));
  }, [getCountryWiseProvincesHandler.data?.data, provinces]);

  // handler function to update province declared here
  const updateProvinceHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      // const [country, ISOCode] = selectedCountry.split(',');

      const body: ProvinceReqBody = {
        country: selectedCountry.id,
        province_code: values.province.code,
        province_name: values.province.name,
        legal_age: values.legal_age,
        gst: values.gst,
        hst: values.hst,
        pst: values.pst,
        updated_by: currentUser?.username,
      };

      console.log('UPDATE PROVINCE REQ BODY: ', body);

      await updateProvinceByIdHandler
        .mutateAsync({ provinceId: province.id, body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Province Updated!!');

            handleModalClose();

            setTargetedProvince(null);
            setSearchText('');

            queryClient.invalidateQueries({ queryKey: ['province-list'] });
          } else {
            toast.error(res.message || 'Province Updating Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering edit province modal component here
  return (
    <ParentModal onClick={handleModalClose}>
      <ModalTitle titleText='update province' />

      <Formik
        initialValues={{
          country: SELECT_FIELD_DEFAULT_VALUE,
          province: {
            id: 0,
            name: province.province_name,
            code: province.province_code,
          },
          legal_age: province.legal_age || '',
          gst: province.gst || '',
          hst: province.hst || '',
          pst: province.pst || '',
        }}
        validationSchema={addEditProvinceSchema}
        onSubmit={updateProvinceHandler}
      >
        {(props) => (
          <Form className='space-y-4'>
            <AcademiaCountrySelect
              name='country'
              label='Select Country'
              setCountry={setSelectedCountry}
              defaultCountryCode={province.iso_code}
              isRequired
              isDisabled
            />

            {getCountryWiseProvincesHandler.isLoading ? (
              <AcademiaModalLoading />
            ) : getCountryWiseProvincesHandler.isSuccess ? (
              <>
                <AcademiaFormSelect
                  name='province'
                  label='Select Province'
                  options={provinceOptions}
                  defaultValue={props.values.province}
                  showSearchInput
                  isRequired
                />

                <AcademiaInputField
                  type='number'
                  name='legal_age'
                  label='Legal Age'
                  placeholder='Enter legal age of the province here...'
                  isRequired
                />

                <AcademiaInputField
                  type='number'
                  name='gst'
                  label='Goods & Services Tax'
                  placeholder='Enter GST rate here... (E.g. 10)'
                  isRequired
                />

                <AcademiaInputField
                  type='number'
                  name='hst'
                  label='Harmonized Sales Tax'
                  placeholder='Enter HST rate here... (E.g. 10)'
                  isRequired
                />

                <AcademiaInputField
                  type='number'
                  name='pst'
                  label='Provincial Sales Tax'
                  placeholder='Enter PST rate here... (E.g. 10)'
                  isRequired
                />
              </>
            ) : getCountryWiseProvincesHandler.isError ? (
              <NoTableDataFound message='Failed to load provinces!!' />
            ) : null}

            <AcademiaButton
              title={
                props.values.province.id === -1
                  ? 'Please, select a province first!'
                  : ''
              }
              type='submit'
              variant='premium'
              icon={<RxUpdate className='size-6' />}
              isLoading={props.isSubmitting}
              isDisabled={props.values.province.id === -1}
              fullWidth
            >
              Update province
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default EditProvinceModal;
